import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { GenericTable } from '@app/components/GenericTable/GenericTable';
import { ReactNode } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ListResponse } from '@app/models';
import { DateRangePicker } from '@app/components/DateRangePicker/DateRangePicker';
import { BusinessesSelect } from '@app/components/BusinessesSelect/BusinessesSelect';
import SearchIcon from '@app/assets/icons/search.svg?react';
import CalendarIcon from '@app/assets/icons/calendar-01.svg?react';
import { OrderDir, OrderDirType } from '@app/constants/core';
import { Select } from '@app/components/Select/Select';
import {ExternalReviewSyncStatus, ExternalReviewSyncType, getReviewsSyncStatusColor, getReviewsSyncTypeColor, ReviewsSyncInterface} from "@app/models/reviews-sync.ts";
import {getPaginatedReviewSyncs} from "@app/services/reviews-sync.ts";
import {convertEnumToOptions, formatEnumLabel} from "@app/utils/utils.tsx";
import {IntegrationLabels, IntegrationType} from "@app/utils/consts.tsx";
// import {getPaginatedReviewSyncs} from "@app/services/reviews-sync.ts";

const HEADER_COLUMNS = [
  { label: 'Date', field: 'updatedAt', sortable: true },
  { label: 'Business', field: 'business', sortable: false },
  { label: 'Type', field: 'type', sortable: false },
  { label: 'Integration', field: 'sourceType', sortable: false },
  { label: 'Status', field: 'status', sortable: false },
  { label: 'Updated', field: 'reviewsUpdated', sortable: false },
  { label: 'Added', field: 'reviewsAdded', sortable: false },
];

const STATUS_FILTER_OPTIONS = convertEnumToOptions(ExternalReviewSyncStatus);
const TYPE_FILTER_OPTIONS = convertEnumToOptions(ExternalReviewSyncType, [ExternalReviewSyncType.MANUAL]);
const INTEGRATION_FILTER_OPTIONS = convertEnumToOptions(IntegrationType, [IntegrationType.YELP, IntegrationType.FACEBOOK, IntegrationType.TRUSTPILOT]);


interface ReviewsSyncsResponse extends ListResponse<ReviewsSyncInterface> {}

interface FormType {
  page: number;
  perPage: number;
  businessId?: string;
  startDate?: string;
  endDate?: string;

  status?: string;
  sourceType?: string;
  type?: string;

  sortBy?: 'udpatedAt' | 'createdAt';
  orderDir: OrderDirType;
}

export const AdminReviewsSyncs = () => {
  const form = useForm<FormType>({
    defaultValues: {
      page: 1,
      perPage: 10,
      businessId: undefined,
      startDate: '',
      endDate: '',
      status: undefined,
      sourceType: undefined,
      type: undefined,
      sortBy: 'udpatedAt',
      orderDir: OrderDir.desc,
    },
  });

  const {
    page,
    perPage,
    businessId,
    startDate,
    endDate,
    status,
    sourceType,
    type,
    sortBy,
    orderDir,
  } = form.watch();

  const { data, isLoading, isError } = useQuery<ReviewsSyncsResponse>({
    queryKey: [
      'reviews-syncs',
      'list',
      {
        page,
        perPage,
        businessId,
        startDate,
        endDate,
        status,
        sourceType,
        type,
        sortBy,
        orderDir,
      },
    ],
    queryFn: () =>
      getPaginatedReviewSyncs({
        page,
        perPage,
        sortBy,
        orderDir,
        ...(businessId && { businessId }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(status && { status }),
        ...(sourceType && { sourceType }),
        ...(type && { type }),
      }),
    placeholderData: keepPreviousData,
  });


  const totalPages = useMemo(() => {
    if (!data) return 0;
    const responseData = data as ReviewsSyncsResponse;
    return Math.ceil(responseData.total / responseData.perPage);
  }, [data]);

  const handlePrevPage = () => {
    if (page > 1) {
      form.setValue('page', page - 1);
    }
  };

  const handleNextPage = () => {
    if (data && page < totalPages) {
      form.setValue('page', page + 1);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Reviews synchronizations</h2>
          <div className="w-64 h-9 bg-gray-200 rounded-md animate-pulse"></div>
        </div>
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="h-16 bg-gray-100 rounded-md animate-pulse"
            ></div>
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-900">Reviews synchronizations</h2>
        <div className="p-4 bg-red-50 text-red-700 rounded-md">
          Error loading reviews synchronizations. Please try again later.
        </div>
      </div>
    );
  }

  const handleLimitChange = (limit?: number) => {
    if (limit) {
      form.setValue('perPage', limit);
      form.setValue('page', 1);
    }
  };

  const handleSort = () => {
    form.setValue('orderDir', orderDir === 'desc' ? 'asc' : 'desc');
  };

  // const handleUserSelect = (selectedUserId?: string) => {
  //   form.setValue('userId', selectedUserId || undefined);
  //   form.setValue('page', 1);
  // };

  const handleBusinessSelect = (selectedBusinessId?: string) => {
    form.setValue('businessId', selectedBusinessId || undefined);
    form.setValue('page', 1);
  };

  const handleStatusSelect = (selectedStatus?: ExternalReviewSyncStatus) => {
    form.setValue('status', selectedStatus || undefined);
    form.setValue('page', 1);
  };

  const handleTypeSelect = (selectedType?: ExternalReviewSyncType) => {
    form.setValue('type', selectedType || undefined);
    form.setValue('page', 1);
  };

  const handleSourceTypeSelect = (selectedSourceType?: IntegrationType) => {
    form.setValue('sourceType', selectedSourceType || undefined);
    form.setValue('page', 1);
  };

  const renderReviewsSyncRow = (syncs: ReviewsSyncInterface[]): ReactNode => {
    return syncs.map((sync) => (
      <tr
        key={sync.id}
        className="hover:bg-gray-50 cursor-pointer"
      >
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {format(new Date(sync.updatedAt), 'PPpp')}
        </td>
        <td className="px-6 py-4">
          {sync.business ? (
            <div>
              <div className="text-sm font-medium text-gray-900">
                {sync.business.name}
              </div>
              <div className="text-sm text-gray-500">{sync.business.email}</div>
            </div>
          ) : (
            <span className="text-sm text-gray-500">-</span>
          )}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getReviewsSyncTypeColor(sync.type)}`}>
            {formatEnumLabel(sync.type)}
          </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          { IntegrationLabels[sync.sourceType] }
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getReviewsSyncStatusColor(sync.status)}`}>
            {formatEnumLabel(sync.status)}
          </span>
        </td>
        <td className="px-6 py-4">
          {sync.reviewsUpdated ?? 0}
        </td>
        <td className="px-6 py-4">
          {sync.reviewsAdded ?? 0}
        </td>
      </tr>
    ));
  };

  const handleFiltersReset = () => {
    form.reset();
    form.setValue('page', 1);
  };

  const hasActiveFilters = Boolean(
    businessId || startDate || endDate || status || sourceType || type
  );
  const hasData = Boolean(data?.data && data.data.length > 0);

  return (
    <FormProvider {...form}>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-2xl font-bold text-gray-900">Reviews Synchronizations</h2>
        </div>
        <div className="bg-white w-full p-4 space-y-4">
          <div className="flex flex-row gap-4 flex-wrap">
            <DateRangePicker
              startName="startDate"
              endName="endDate"
              startPlaceholder="Start date"
              endPlaceholder="End date"
              className="bg-gray-100 border-0 rounded-lg px-4 py-2 text-gray-600 placeholder:text-gray-500 focus:bg-gray-200 focus:outline-none !ml-0"
              containerClass="flex-shrink-0"
              position="absolute"
            />

            {/*<Select*/}
            {/*    options={ACTIVITED_FILTER_OPTIONS}*/}
            {/*    onChange={(value) => handleActivatedFilterSelect(value)}*/}
            {/*    value={activatedFilter || ''}*/}
            {/*    name="activatedFilter"*/}
            {/*    placeholder="All"*/}
            {/*/>*/}
            <BusinessesSelect
              onChange={handleBusinessSelect}
              value={businessId}
              name="businessId"
            />
            {/*<Select*/}
            {/*    options={STATUS_FILTER_OPTIONS}*/}
            {/*    onChange={(value) => handleActivatedFilterSelect(value)}*/}
            {/*    value={activatedFilter || ''}*/}
            {/*    name="status"*/}
            {/*    placeholder="All"*/}
            {/*/>*/}
            <Select
              options={STATUS_FILTER_OPTIONS}
              onChange={(value) => handleStatusSelect(value as ExternalReviewSyncStatus)}
              value={status || ''}
              name="status"
              placeholder="All Statuses"
            />
            <Select
              options={TYPE_FILTER_OPTIONS}
              onChange={(value) => handleTypeSelect(value as ExternalReviewSyncType)}
              value={type || ''}
              name="type"
              placeholder="All Types"
            />
            <Select
              options={INTEGRATION_FILTER_OPTIONS}
              onChange={(value) => handleSourceTypeSelect(value as IntegrationType)}
              value={sourceType || ''}
              name="sourceType"
              placeholder="All Integrations"
            />
            {hasActiveFilters && (
              <button
                type="button"
                onClick={handleFiltersReset}
                className="flex items-center gap-1 text-red-500 hover:text-red-600 transition-colors cursor-pointer"
              >
                <span className="text-sm">Reset</span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 4L4 12M4 4L12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
        {!hasData && !isLoading && hasActiveFilters && (
          <>
            <br />
            <br />
            <br />
            <div className="flex items-center justify-center flex-col max-w-[350px] mx-auto space-y-4 h-full mt-20">
              <div className="rounded-lg bg-white p-4">
                <SearchIcon />
              </div>
              <p className="font-bold">No audit logs</p>
              <p className="text-sm text-base-coolGray text-center">
                No reviews synchronizations match your current filters.
                <br /> Consider adjusting them.
              </p>
            </div>
          </>
        )}
        {!hasData && !isLoading && !hasActiveFilters && (
          <div className="flex items-center justify-center flex-col max-w-[350px] mx-auto space-y-4 h-full mt-20">
            <div className="rounded-lg bg-white p-4">
              <CalendarIcon />
            </div>
            <p className="font-bold">No audit logs</p>
            <p className="text-sm text-base-coolGray text-center">
              There are no reviews synchronizations to display.
              <br />
              Reviews synchronizations will appear here when they are performed.
            </p>
          </div>
        )}
        {data?.data && data.data!.length > 0 && !isLoading && (
          <div className="overflow-auto bg-white">
            <GenericTable<ReviewsSyncInterface>
              data={data?.data || []}
              headers={HEADER_COLUMNS}
              renderRow={renderReviewsSyncRow}
              currentPage={page}
              isLoading={isLoading}
              currentSortBy="updatedAt"
              currentSortOrder={orderDir as 'asc' | 'desc'}
              onSort={handleSort}
              pagination={{
                total: data?.total || 0,
                handleNextPage,
                handlePrevPage,
                handleLimitChange,
              }}
            />
          </div>
        )}
      </div>
    </FormProvider>
  );
};

export default AdminReviewsSyncs;
